import { ArrowRight, Building2, CheckCircle2, AlertTriangle, Info, Loader2, RefreshCw } from "lucide-react";
import { WhatsAppIcon } from "../BrandIcons";
import { contactChannels, formatCnpj, type ContactChannel } from "./contactChannels";
import type { CompanyInfo } from "./useCnpjValidation";

export function ChannelSelectionStep({ onSelect }: { onSelect: (c: ContactChannel) => void }) {
  return (
    <div className="flex flex-col gap-2.5">
      {contactChannels.map((channel) => {
        const Icon = channel.icon;
        return (
          <button
            key={channel.id}
            type="button"
            onClick={() => onSelect(channel)}
            className="group flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-all duration-200 hover:border-brand-blue/40 hover:bg-slate-50 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/50 active:scale-[0.99] min-h-[64px]"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-blue/10 text-brand-blue transition-colors group-hover:bg-brand-blue group-hover:text-white">
              <Icon className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-bold uppercase tracking-wide text-brand-blue">
                {channel.title}
              </span>
              <span className="block text-xs text-slate-500">{channel.description}</span>
            </span>
            <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-brand-red" />
          </button>
        );
      })}
    </div>
  );
}

export function CnpjQuestionStep({
  onAnswer,
}: {
  onAnswer: (hasCnpj: boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <button
        type="button"
        onClick={() => onAnswer(true)}
        className="flex w-full items-center gap-3 rounded-xl border border-brand-blue/30 bg-brand-blue/5 px-4 py-4 text-left font-bold text-brand-blue transition-all duration-200 hover:bg-brand-blue hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/50 active:scale-[0.99] min-h-[56px]"
      >
        <Building2 className="h-5 w-5 shrink-0" />
        <span className="flex-1 text-sm">Sim, tenho CNPJ</span>
        <ArrowRight className="h-4 w-4 shrink-0" />
      </button>
      <button
        type="button"
        onClick={() => onAnswer(false)}
        className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-4 text-left font-bold text-slate-600 transition-all duration-200 hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 active:scale-[0.99] min-h-[56px]"
      >
        <Info className="h-5 w-5 shrink-0 text-slate-400" />
        <span className="flex-1 text-sm">Não tenho CNPJ</span>
      </button>
    </div>
  );
}

export function NoCnpjStep({
  title,
  text,
  onBack,
}: {
  title: string;
  text: string;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div>
            <p className="text-sm font-bold text-slate-800">{title}</p>
            <p className="mt-1 text-xs leading-relaxed text-slate-600">{text}</p>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onBack}
        className="w-full rounded-xl bg-brand-blue px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-blue/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/50 min-h-[48px]"
      >
        Voltar aos canais de atendimento
      </button>
    </div>
  );
}

interface CnpjEntryProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  loading: boolean;
  error: string | null;
  canRetry: boolean;
}

export function CnpjEntryStep({ value, onChange, onSubmit, loading, error, canRetry }: CnpjEntryProps) {
  const digits = value.replace(/\D/g, "");
  const ready = digits.length === 14;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (ready && !loading) onSubmit();
      }}
      className="flex flex-col gap-3"
    >
      <div>
        <label htmlFor="modal-cnpj" className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-700">
          CNPJ
        </label>
        <input
          id="modal-cnpj"
          name="cnpj"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="00.000.000/0000-00"
          value={value}
          disabled={loading}
          onChange={(e) => onChange(formatCnpj(e.target.value))}
          className={`w-full rounded-lg border bg-slate-50 px-4 py-3 text-sm text-slate-700 transition-colors focus:outline-none focus:ring-1 disabled:opacity-60 ${
            error
              ? "border-red-400 focus:border-red-400 focus:ring-red-400"
              : "border-slate-200 focus:border-brand-blue focus:ring-brand-blue"
          }`}
        />
        {error && (
          <p className="mt-1.5 flex items-start gap-1.5 text-xs text-red-600">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={!ready || loading}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-blue px-4 py-3 text-sm font-bold text-white transition-all hover:bg-brand-blue/90 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/50 min-h-[48px]"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Consultando CNPJ...
          </>
        ) : canRetry ? (
          <>
            <RefreshCw className="h-4 w-4" />
            Tentar novamente
          </>
        ) : (
          "Validar CNPJ"
        )}
      </button>
    </form>
  );
}

export function CnpjSuccessStep({
  company,
  onContinue,
}: {
  company: CompanyInfo;
  onContinue: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border border-green-200 bg-green-50/70 p-4">
        <p className="flex items-center gap-2 text-sm font-bold text-green-700">
          <CheckCircle2 className="h-4 w-4" /> Empresa encontrada
        </p>
        <div className="mt-2 space-y-0.5 text-xs text-slate-600">
          {company.legalName && <p className="font-semibold text-slate-800">{company.legalName}</p>}
          {company.tradeName && company.tradeName !== company.legalName && <p>{company.tradeName}</p>}
          {(company.city || company.state) && (
            <p>
              {company.city}
              {company.city && company.state ? "/" : ""}
              {company.state}
            </p>
          )}
          <p>CNPJ {formatCnpj(company.cnpj)}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onContinue}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-green-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600/50 min-h-[48px]"
      >
        <WhatsAppIcon className="h-5 w-5" />
        Continuar para o WhatsApp
      </button>
    </div>
  );
}

export function BlockedStep({
  title,
  text,
  onBack,
  backLabel = "Corrigir CNPJ",
}: {
  title: string;
  text: string;
  onBack: () => void;
  backLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div>
            <p className="text-sm font-bold text-slate-800">{title}</p>
            <p className="mt-1 text-xs leading-relaxed text-slate-600">{text}</p>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onBack}
        className="w-full rounded-xl bg-brand-blue px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-blue/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/50 min-h-[48px]"
      >
        {backLabel}
      </button>
    </div>
  );
}
